import { FormikValues } from 'formik';
import {
    focVarsFromField,
    focFieldRegex,
    qualityFieldRegex,
    qualityVarsFromField,
    enumValue,
} from '~/util';
import { moment, momentFormats } from '~/vendorConfigs/moment';

function checkboxValues(values: FormikValues, regex: RegExp) {
    const checkboxes: { [key: string]: boolean } = {};
    Object.entries(values).forEach(([fieldId, value]) => {
        const fieldMatch = fieldId.match(regex);
        if (fieldMatch?.[0]) {
            checkboxes[fieldMatch[1]] = value;
        }
    });
    return checkboxes;
}

export function focCheckboxValues(values: FormikValues) {
    return checkboxValues(values, focFieldRegex.checkbox);
}

export function qualityCheckboxValues(values: FormikValues) {
    return checkboxValues(values, qualityFieldRegex.checkbox);
}

interface SubmissionAnswerMappingType {
    [key: number]: SubmissionAnswerResponseType;
}

export function buildFocSubmissionAnswers(values: null | FormikValues) {
    if (!values) return [];
    const submissionAnswers: SubmissionAnswerMappingType = {};
    const checkboxes = focCheckboxValues(values);

    Object.entries(values).forEach(([fieldId, value]) => {
        const { fieldType, detailId, detailSystemId, actionId } = focVarsFromField(fieldId)!;
        if (actionId === null || !checkboxes[actionId]) return;
        // Checkbox is checked, save this response
        if (!submissionAnswers[detailId!]) {
            submissionAnswers[detailId!] = {
                foc_action_detail: {
                    id: detailId!,
                    system_id: detailSystemId!,
                },
                date: '',
                value: '',
            };
        }

        switch (fieldType) {
            case 'answer':
                submissionAnswers[detailId!].value = value;
                break;
            case 'date':
                submissionAnswers[detailId!].date = value
                    ? moment(value).format(momentFormats.LR_API_DATETIME)
                    : '';
                break;
            case 'system_id':
                submissionAnswers[detailId!].foc_action_detail!.system_id = value;
                break;
            default:
                break;
        }
    });

    return Object.values(submissionAnswers).filter((answer) => answer.date && answer.value);
}

export function buildQualitySubmissionAnswers(values: null | FormikValues) {
    if (!values) return [];
    const submissionAnswers: SubmissionAnswerMappingType = {};
    const checkboxes = qualityCheckboxValues(values);

    Object.entries(values).forEach(([fieldId, value]) => {
        const { fieldType, dataType, detailId, systemId } = qualityVarsFromField(fieldId);
        if (detailId === null || !checkboxes[detailId]) return;
        // Checkbox is checked, save this response
        if (!submissionAnswers[detailId]) {
            submissionAnswers[detailId] = {
                quality_measure_detail: {
                    id: detailId,
                    system_id: systemId,
                },
                date: '',
                value: '',
            };
        }

        switch (fieldType) {
            case 'answer':
                if (dataType === enumValue(['gap', 'supplemental_value_data_types', 'Ratio'])) {
                    const answer2 =
                        values[
                            `quality_measure_detail_${detailId}_${systemId}_${dataType}_answer2`
                        ];
                    submissionAnswers[detailId].value = `${value.trim()}/${answer2.trim()}`;
                } else {
                    submissionAnswers[detailId].value =
                        typeof value === 'string' ? value.trim() : value;
                }
                break;
            case 'answer2':
                break;
            case 'date':
                submissionAnswers[detailId].date = value
                    ? moment(value).format(momentFormats.LR_API_DATETIME)
                    : '';
                submissionAnswers[detailId].quality_measure_detail!.system_id = systemId;
                break;
            default:
                break;
        }
    });
    return Object.values(submissionAnswers);
}

function buildCollectionAnswers(
    collection: undefined | GapDetailType[],
    submissionAnswers: { [key: number]: SubmissionAnswerResponseType },
    isSingle: boolean,
    checkboxId: (detail: GapDetailType) => string,
    dateId: (detail: GapDetailType) => string,
    answerId: (detail: GapDetailType) => string | null
) {
    const newState: { [key: string]: null | Date | boolean | string | number } = {};
    collection?.forEach((detail) => {
        const existingSubmission = submissionAnswers[detail.id];
        if (existingSubmission) {
            newState[dateId(detail)] = new Date(existingSubmission.date.slice(0, -6));
            // Double check for Invalid Date object
            if ((newState[dateId(detail)] as Date).toString() === 'Invalid Date') {
                newState[dateId(detail)] = null;
            }

            newState[checkboxId(detail)] = true;
            if (detail.data_type === enumValue(['gap', 'supplemental_value_data_types', 'Ratio'])) {
                const [part1, part2] = (existingSubmission.value as string).split('/');
                newState[answerId(detail)!] = part1;
                newState[`${answerId(detail)}2`] = part2;
            } else {
                newState[answerId(detail)!] = existingSubmission.value;
            }
        } else {
            if (!newState[checkboxId(detail)]) {
                // Don't overwrite checkbox if another detail has it checked
                newState[checkboxId(detail)] = false;
            }
            if (isSingle) {
                newState[checkboxId(detail)] = true;
            }
            newState[dateId(detail)] = null;
            newState[answerId(detail)!] = '';
            if (detail.data_type === enumValue(['gap', 'supplemental_value_data_types', 'Ratio'])) {
                newState[`${answerId(detail)}2`] = '';
            }
        }
    });
    return newState;
}

export function initFocExistingAnswers(
    submissionAnswer: undefined | SubmissionAnswerType,
    collection: GapDetailType[],
    action: FocActionType,
    isSingle: boolean
) {
    const submissionAnswers: SubmissionAnswerMappingType = {};
    if (submissionAnswer?.metadata?.responses) {
        submissionAnswer.metadata.responses.forEach((response) => {
            submissionAnswers[response.foc_action_detail!.id] = response;
        });
    }
    return buildCollectionAnswers(
        collection,
        submissionAnswers,
        isSingle,
        () => `foc_action_${action.id}_checked`,
        (detail) => `foc_gap_action_detail_${action.id}_${detail.id}_${detail.system_id}_date`,
        (detail) => `foc_gap_action_detail_${action.id}_${detail.id}_${detail.system_id}_answer`
    );
}

export function initQualityExistingAnswers(
    submissionAnswer: undefined | SubmissionAnswerType,
    collection: undefined | GapDetailType[],
    isSingle: boolean
) {
    const submissionAnswers: SubmissionAnswerMappingType = {};
    if (submissionAnswer?.metadata?.responses) {
        submissionAnswer.metadata.responses.forEach((response) => {
            submissionAnswers[response.quality_measure_detail!.id] = response;
        });
    }
    return buildCollectionAnswers(
        collection,
        submissionAnswers,
        isSingle,
        (detail: GapDetailType) =>
            `quality_measure_detail_${detail.id}_${detail.system_id}_checked`,
        (detail: GapDetailType) =>
            `quality_measure_detail_${detail.id}_${detail.system_id}_${detail.data_type || 0}_date`,
        (detail: GapDetailType) =>
            detail.data_type === enumValue(['gap', 'supplemental_value_data_types', 'NoValue'])
                ? null
                : `quality_measure_detail_${detail.id}_${detail.system_id}_${
                      detail.data_type || 0
                  }_answer`
    );
}
